/** A point on a drawing. A line between steps runs in straight runs with 45° turns. */
export type Pt = [number, number];

/**
 * The builder's lines, routed together (Jason, 2026-10-05: "lines can not go
 * behind nodes or overlap other lines"; "dependency lines need to have a
 * minimum offset distance from a node, and join or leave shared node at a 45
 * degree angle for a little"). A line leaves a node's foot, or a tab's dot,
 * straight down; turns into the gap under its row; runs across on a track of
 * its own; and turns down into the top of the node that waits on it. Where
 * that drop would pass a box, it runs down a corridor between the columns
 * instead (up it, when its target sits higher), the one where it crosses fewest
 * other lines, round the outside if need be (#78), and across in the gap above
 * its target. Every line keeps its distance from the boxes it passes, runs straight
 * for that far out of a node and into one, and turns with a short 45° cut, so
 * lines sharing a node fork out of it, or join into it, at 45°. Lines side by
 * side keep a track apart. A step opened draws its chain with it; the board
 * lays its own lines in channels (channels.ts) and draws with it only a line
 * back up the board.
 */

export interface RouteBox {
  x: number;
  y: number;
  w: number;
  /** The box alone. */
  h: number;
  /** Tabs hanging from its foot, if any: the room they take. */
  tabs: number;
}

export interface RouteWant {
  id: string;
  src: string;
  dst: string;
  /** Where it leaves the source: the foot, or a tab's dot. */
  from: Pt;
  /** Where it arrives: the tip of its arrow, above the target. */
  to: Pt;
}

export interface Routed {
  d: string;
  /** A point on the line to hang its menu from. */
  mid: Pt;
  /** The line as drawn: straight runs and their 45° cuts. */
  points: Pt[];
}

/** How much room the lines are given, in the drawing's own units. */
export interface RouteRoom {
  /** Between two lines side by side. */
  track: number;
  /** From any box a line passes; and straight out of a node before its first turn, and into one after its last. */
  clear: number;
  /** The 45° cut at a turn. */
  bevel: number;
  /** The arrow at a line's end: `to` is its tip. */
  head: number;
  /** Above a box, what its arrows take: from its top up to the arrows' far end. */
  above: number;
  /** Under a box (and its tabs), down to the dots the lines leave from. */
  below: number;
}

/** The board's and focus's: boxes drawn full size, the arrow's tip 9 above a box. */
export const BOARD_ROOM: RouteRoom = { track: 6, clear: 12, bevel: 10, head: 8, above: 17, below: 4 };

interface Zone {
  id: string;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

interface Run {
  /** Across, from x to x (unordered), at y. */
  xa: number;
  xb: number;
  top: number;
  bottom: number;
  y: number;
  /** Where it turns at either end, and whether the line goes on up from there (to its source, or up a corridor) or down. */
  ends: { x: number; up: boolean }[];
  /** The point it leaves from, and the side it goes off to; the point it comes into, and the side it comes from: two
   * runs off one point on its two sides make one fork, or one join, at 45°, and may share a track. */
  leaves: string | null;
  leaveSide: number;
  joins: string | null;
  joinSide: number;
}

interface Plan {
  w: RouteWant;
  kind: 'straight' | 'direct' | 'detour';
  first?: Run;
  last?: Run;
  /** A detour's upright: from the gap under its source to the gap above its target, in a corridor between the boxes. */
  under?: number;
  over?: number;
  x?: number;
}

const PAPER = 2;
const tidy = (v: number) => Math.round(v * 10) / 10;
const turn = (a: Pt, b: Pt, c: Pt) => Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
/** Whether two runs cross through each other: one only touching the other, or lying along it, is not a crossing. */
export const crosses = (a: Pt, b: Pt, c: Pt, d: Pt): boolean => turn(a, b, c) * turn(a, b, d) < 0 && turn(c, d, a) * turn(c, d, b) < 0;
const spot = ([x, y]: Pt) => `${tidy(x)},${tidy(y)}`;

export function routeLines(wants: RouteWant[], boxes: ReadonlyMap<string, RouteBox>, room: RouteRoom = BOARD_ROOM, width = Infinity): Map<string, Routed> {
  const { track, clear, bevel, head, above, below } = room;
  // No line runs off the paper: as far in from its right edge, when its width is given, as from its left.
  const right = width - PAPER;
  const zones: Zone[] = [...boxes.entries()].map(([id, b]) => ({
    id,
    x1: b.x - clear,
    x2: b.x + b.w + clear,
    y1: b.y - above - clear,
    y2: b.y + b.h + b.tabs + below + clear,
  }));
  const spanning = (xa: number, xb: number) => {
    const lo = Math.min(xa, xb);
    const hi = Math.max(xa, xb);
    return zones.filter((z) => z.x1 < hi + 0.5 && z.x2 > lo - 0.5);
  };
  /** The free band from `from` down, across xa..xb: pushed down past any box in the way, ending at the next. */
  const bandBelow = (xa: number, xb: number, from: number): [number, number] => {
    const zs = spanning(xa, xb);
    let y = from;
    for (let moved = true; moved; ) {
      moved = false;
      for (const z of zs)
        if (y > z.y1 && y < z.y2) {
          y = z.y2;
          moved = true;
        }
    }
    return [y, Math.min(Infinity, ...zs.filter((z) => z.y1 >= y).map((z) => z.y1))];
  };
  /** The free band from `to` up, across xa..xb. */
  const bandAbove = (xa: number, xb: number, to: number): [number, number] => {
    const zs = spanning(xa, xb);
    let y = to;
    for (let moved = true; moved; ) {
      moved = false;
      for (const z of zs)
        if (y > z.y1 && y < z.y2) {
          y = z.y1;
          moved = true;
        }
    }
    return [Math.max(-Infinity, ...zs.filter((z) => z.y2 <= y).map((z) => z.y2)), y];
  };
  /**
   * A detour's runs across keep to the gap right under its source's row, and right above its target's: the band, ended
   * at the next box below (or above) anywhere across, so a run never slides down a stretch of empty columns into a gap
   * further on and turns back up its corridor from there.
   */
  const gapBelow = (xa: number, xb: number, from: number): [number, number] => {
    const [top, bottom] = bandBelow(xa, xb, from);
    return [top, Math.min(bottom, ...zones.filter((z) => z.y1 >= top).map((z) => z.y1))];
  };
  const gapAbove = (xa: number, xb: number, to: number): [number, number] => {
    const [top, bottom] = bandAbove(xa, xb, to);
    return [Math.max(top, ...zones.filter((z) => z.y2 <= bottom).map((z) => z.y2)), bottom];
  };
  /** Nothing but these within reach of an upright at x from ya to yb. */
  const clearUpright = (x: number, ya: number, yb: number, skip: string[]) =>
    !zones.some((z) => !skip.includes(z.id) && x > z.x1 && x < z.x2 && Math.max(ya, yb) > z.y1 && Math.min(ya, yb) < z.y2);
  /** The corridors between the boxes from ya to yb, as ranges of x. */
  const corridors = (ya: number, yb: number): [number, number][] => {
    const lo = Math.min(ya, yb);
    const hi = Math.max(ya, yb);
    const blocked = zones
      .filter((z) => z.y2 > lo && z.y1 < hi)
      .map((z) => [z.x1, z.x2] as const)
      .sort((a, b) => a[0] - b[0]);
    const free: [number, number][] = [];
    let x = PAPER;
    for (const [a, b] of blocked) {
      if (a > x) free.push([x, a]);
      x = Math.max(x, b);
    }
    free.push([x, right]);
    return free;
  };

  const plans: Plan[] = [];
  for (const w of wants) {
    if (!boxes.has(w.src) || !boxes.has(w.dst)) continue;
    const [sx, sy] = w.from;
    const [tx, ty] = w.to;
    // Straight out of the source before the first turn, straight into the target after the last.
    const leave = sy + clear + bevel;
    const arrive = ty - head - clear - bevel;
    if (Math.abs(sx - tx) < 0.5 && ty > sy && clearUpright(sx, sy, ty, [w.src, w.dst])) {
      plans.push({ w, kind: 'straight' });
      continue;
    }
    if (ty > sy) {
      const [top, bottom] = bandBelow(sx, tx, leave);
      const last = Math.min(bottom, arrive);
      if (top <= last && clearUpright(sx, sy, top, [w.src]) && clearUpright(tx, top, ty, [w.dst])) {
        const side = Math.sign(tx - sx);
        const ends = [
          { x: sx, up: true },
          { x: tx, up: false },
        ];
        plans.push({ w, kind: 'direct', first: { xa: sx, xb: tx, top, bottom: last, y: top, ends, leaves: spot(w.from), leaveSide: side, joins: spot(w.to), joinSide: -side } });
        continue;
      }
    }
    // Round the boxes in the way: down (or up) a corridor between them, chosen below with the others.
    plans.push({ w, kind: 'detour', under: bandBelow(sx, sx, leave)[0], over: bandAbove(tx, tx, arrive)[1] });
  }

  // Each upright on a track of its own in the corridor where it crosses fewest of the other lines, then nearest its
  // target (#78, Jason, 2026-10-09: "it could have gone left then down and then back right and avoid all crossings"), a
  // track apart from any beside it; a corridor full there spills into the next best. A line is weighed by where it must
  // cross another whatever the tracks: its runs across at the middle of their gaps, so two in one gap never count (their
  // tracks keep them apart, below), and an upright through a gap another runs across in always does. The longest first:
  // they have the fewest corridors to choose from; then each again, among all the others'.
  const reach = (p: Plan) => Math.abs(p.under! - p.over!);
  const detours = plans.filter((p) => p.kind === 'detour').sort((a, b) => reach(b) - reach(a));
  const middle = ([a, b]: [number, number]) => (Number.isFinite(a) && Number.isFinite(b) ? (a + b) / 2 : Number.isFinite(a) ? a + 2 * track : b - 2 * track);
  const skeleton = (p: Plan, x = p.x): Pt[] => {
    const [sx, sy] = p.w.from;
    const [tx, ty] = p.w.to;
    if (p.kind === 'straight') return [p.w.from, p.w.to];
    if (p.kind === 'direct') {
      const y = middle([p.first!.top, p.first!.bottom]);
      return [p.w.from, [sx, y], [tx, y], p.w.to];
    }
    if (x === undefined) return [];
    const y1 = middle(gapBelow(sx, x, sy + clear + bevel));
    const y2 = middle(gapAbove(x, tx, ty - head - clear - bevel));
    return [p.w.from, [sx, y1], [x, y1], [x, y2], [tx, y2], p.w.to];
  };
  const skeletons = new Map<Plan, Pt[]>(plans.filter((p) => p.kind !== 'detour').map((p) => [p, skeleton(p)]));
  const crossingsWith = (line: Pt[], self: Plan) => {
    let n = 0;
    for (const [q, other] of skeletons) {
      if (q === self) continue;
      for (let i = 1; i < line.length; i++)
        for (let j = 1; j < other.length; j++) if (crosses(line[i - 1]!, line[i]!, other[j - 1]!, other[j]!)) n++;
    }
    return n;
  };
  const uprights = new Map<Plan, { x: number; lo: number; hi: number }>();
  for (let round = 0; round < 2; round++)
    for (const p of detours) {
      const lo = Math.min(p.under!, p.over!);
      const hi = Math.max(p.under!, p.over!);
      const [sx] = p.w.from;
      const [tx] = p.w.to;
      const free = (x: number) => ![...uprights].some(([q, u]) => q !== p && Math.abs(u.x - x) < track && u.hi > lo && u.lo < hi);
      const cost = (x: number) => Math.abs(x - tx) + Math.abs(x - sx) / 4;
      // Each corridor's tracks, nearest its target first.
      const tracks = corridors(lo, hi).map(([c1, c2]) => {
        const a = c1 + track / 2;
        const b = c2 - track / 2;
        if (b < a) return [];
        const near = Math.min(Math.max(tx, a), b);
        const out: number[] = [];
        for (let i = 0; i < 12; i++) out.push(...[near + i * track, near - i * track].filter((v, j) => v >= a - 0.01 && v <= b + 0.01 && (i > 0 || j === 0)));
        return out;
      });
      // A corridor's first free track, weighed; every corridor full there, the nearest track there is.
      const choices = tracks.flatMap((out) => {
        const x = out.find(free);
        return x === undefined ? [] : [{ x, crossings: crossingsWith(skeleton(p, x), p) }];
      });
      const best = choices.sort((a, b) => a.crossings - b.crossings || cost(a.x) - cost(b.x))[0]?.x ?? tracks.flat().sort((a, b) => cost(a) - cost(b))[0] ?? tx;
      p.x = best;
      uprights.set(p, { x: best, lo, hi });
      skeletons.set(p, skeleton(p));
    }
  for (const p of detours) {
    const x = p.x!;
    const [sx, sy] = p.w.from;
    const [tx, ty] = p.w.to;
    const [t1, b1] = gapBelow(sx, x, sy + clear + bevel);
    const [t2, b2] = gapAbove(x, tx, ty - head - clear - bevel);
    // Down to a target below, the upright leaves the first run downward and comes into the last from above; up to one
    // above, the other way round.
    const down = p.over! > p.under!;
    const first = [
      { x: sx, up: true },
      { x, up: !down },
    ];
    const last = [
      { x, up: down },
      { x: tx, up: false },
    ];
    p.first = { xa: sx, xb: x, top: t1, bottom: b1, y: t1, ends: first, leaves: spot(p.w.from), leaveSide: Math.sign(x - sx), joins: null, joinSide: 0 };
    p.last = { xa: x, xb: tx, top: t2, bottom: b2, y: t2, ends: last, leaves: null, leaveSide: 0, joins: spot(p.w.to), joinSide: Math.sign(x - tx) };
  }

  // Every run across its gap on a track of its own, in the order that keeps lines from crossing: where a line turns up
  // off its run (to its source, or up a corridor) inside another's span, it runs above that one; where it turns down (to
  // its target, or down a corridor), below. So lines forking out of one node go out to the farthest first, highest, and
  // lines joining one node come in from the farthest lowest, and none crosses another at its turn.
  const runs = plans.flatMap((p) => [p.first, p.last].filter((r): r is Run => r !== undefined));
  const lo = (r: Run) => Math.min(r.xa, r.xb);
  const hi = (r: Run) => Math.max(r.xa, r.xb);
  const within = (x: number, r: Run) => x > lo(r) + 0.5 && x < hi(r) - 0.5;
  const sharesGap = (a: Run, b: Run) => a.top <= b.bottom && b.top <= a.bottom && Math.min(hi(a), hi(b)) - Math.max(lo(a), lo(b)) > -1;
  /** a must lie above b. */
  const over = (a: Run, b: Run) => sharesGap(a, b) && (a.ends.some((e) => e.up && within(e.x, b)) || b.ends.some((e) => !e.up && within(e.x, a)));
  const clash = (a: Run, b: Run, y: number) => {
    if (Math.abs(b.y - y) >= track) return false;
    if (Math.min(hi(a), hi(b)) - Math.max(lo(a), lo(b)) < -1) return false;
    // Off one point on its two sides: the two make one fork, or one join.
    if (a.leaves !== null && a.leaves === b.leaves && a.leaveSide !== b.leaveSide) return false;
    if (a.joins !== null && a.joins === b.joins && a.joinSide !== b.joinSide) return false;
    return true;
  };
  // What must lie above what, taken in turn from the top; where two rules would go round in a circle, the higher gap and
  // the longer run go first.
  const higher = new Map(runs.map((r) => [r, runs.filter((q) => q !== r && over(q, r))]));
  const order: Run[] = [];
  const left = new Set(runs);
  const rank = (a: Run, b: Run) => a.top - b.top || hi(b) - lo(b) - (hi(a) - lo(a));
  /** The circles of rules among the runs left (each run in one): runs each of which must, one way round, lie above the other. */
  const circles = () => {
    const index = new Map<Run, number>();
    const low = new Map<Run, number>();
    const stack: Run[] = [];
    const out: Run[][] = [];
    const visit = (r: Run) => {
      index.set(r, index.size);
      low.set(r, index.get(r)!);
      stack.push(r);
      for (const q of higher.get(r)!) {
        if (!left.has(q)) continue;
        if (!index.has(q)) {
          visit(q);
          low.set(r, Math.min(low.get(r)!, low.get(q)!));
        } else if (stack.includes(q)) low.set(r, Math.min(low.get(r)!, index.get(q)!));
      }
      if (low.get(r) === index.get(r)) {
        const ring: Run[] = [];
        for (let q = stack.pop()!; ; q = stack.pop()!) {
          ring.push(q);
          if (q === r) break;
        }
        out.push(ring);
      }
    };
    for (const r of left) if (!index.has(r)) visit(r);
    return out;
  };
  while (left.size) {
    const ready = [...left].filter((r) => higher.get(r)!.every((q) => !left.has(q)));
    let next: Run;
    if (ready.length) next = ready.sort(rank)[0]!;
    else {
      // Where the rules go round in a circle (two lines that must cross), it is broken inside the circle nothing else
      // must lie above, at the run with the fewest of it still to go above it; a rule outside every circle is never broken.
      const ring = circles().filter((c) => c.every((r) => higher.get(r)!.every((q) => !left.has(q) || c.includes(q)))).sort((a, b) => rank(a.sort(rank)[0]!, b.sort(rank)[0]!))[0]!;
      const waiting = (r: Run) => higher.get(r)!.filter((q) => left.has(q) && ring.includes(q)).length;
      next = [...ring].sort((a, b) => waiting(a) - waiting(b) || rank(a, b))[0]!;
    }
    order.push(next);
    left.delete(next);
  }
  const placed: Run[] = [];
  for (const r of order) {
    const top = Number.isFinite(r.top) ? r.top : r.bottom - 5 * track;
    const bottom = Number.isFinite(r.bottom) ? r.bottom : r.top + 5 * track;
    // Below every run placed already that must lie above it.
    const from = Math.max(top, ...placed.filter((q) => over(q, r)).map((q) => q.y + track));
    if (top > bottom) r.y = (top + bottom) / 2;
    else {
      let y = Math.min(from, bottom);
      for (let v = Math.min(from, bottom); v <= bottom; v++)
        if (!placed.some((q) => clash(r, q, v))) {
          y = v;
          break;
        }
      r.y = y;
    }
    placed.push(r);
  }

  const out = new Map<string, Routed>();
  for (const p of plans) {
    const [sx, sy] = p.w.from;
    const [tx, ty] = p.w.to;
    let points: Pt[];
    let mid: Pt;
    if (p.kind === 'straight') {
      points = [
        [sx, sy],
        [tx, ty],
      ];
      mid = [sx, (sy + ty) / 2];
    } else if (p.kind === 'direct') {
      const y = p.first!.y;
      points = [
        [sx, sy],
        [sx, y],
        [tx, y],
        [tx, ty],
      ];
      mid = [(sx + tx) / 2, y];
    } else {
      const x = p.x!;
      const y1 = p.first!.y;
      const y2 = p.last!.y;
      points = [
        [sx, sy],
        [sx, y1],
        [x, y1],
        [x, y2],
        [tx, y2],
        [tx, ty],
      ];
      mid = [x, (y1 + y2) / 2];
    }
    const drawn = bevelled(points, bevel);
    out.set(p.w.id, { d: pathOf(drawn), mid, points: drawn });
  }
  return out;
}

/** A line of straight runs with each turn cut at 45°: never more than half a run between two turns, all of an end's. */
export function bevelled(points: Pt[], cut: number): Pt[] {
  const pts: Pt[] = [];
  for (const p of points) {
    const prev = pts[pts.length - 1];
    if (prev && Math.abs(prev[0] - p[0]) < 0.01 && Math.abs(prev[1] - p[1]) < 0.01) continue;
    // A point on the way, going on as it came, is no turn.
    const before = pts[pts.length - 2];
    if (prev && before && (Math.abs(before[0] - prev[0]) < 0.01 ? Math.abs(prev[0] - p[0]) < 0.01 : Math.abs(before[1] - prev[1]) < 0.01 && Math.abs(prev[1] - p[1]) < 0.01)) pts.pop();
    pts.push(p);
  }
  if (pts.length < 3) return pts.map(([x, y]) => [tidy(x), tidy(y)]);
  const drawn: Pt[] = [pts[0]!];
  for (let i = 1; i < pts.length - 1; i++) {
    const [ax, ay] = pts[i - 1]!;
    const [bx, by] = pts[i]!;
    const [cx, cy] = pts[i + 1]!;
    const into = Math.hypot(bx - ax, by - ay);
    const onward = Math.hypot(cx - bx, cy - by);
    const c = Math.min(cut, i === 1 ? into : into / 2, i === pts.length - 2 ? onward : onward / 2);
    drawn.push([bx - ((bx - ax) / into) * c, by - ((by - ay) / into) * c], [bx + ((cx - bx) / onward) * c, by + ((cy - by) / onward) * c]);
  }
  drawn.push(pts[pts.length - 1]!);
  return drawn.map(([x, y]) => [tidy(x), tidy(y)]);
}

export const pathOf = (points: Pt[]) => points.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ');
